/**
 * Hub order payloads aligned with Chart.tsx / ChartTradePanel patterns.
 */

import type { VybeOrderClosePayload } from "@/vybe-trader-client/connection/client/VybeTraderClientStream";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades/types";
import {
  VybeOrderType,
} from "@/vybe-trader-client/trade-history/types";
import { getTradeClosePrice } from "@/vybe-trader-client/opened-trades/utils";

const PENDING_ORDER_TYPES = new Set<number>([
  VybeOrderType.BuyLimit,
  VybeOrderType.SellLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.SellStop,
  VybeOrderType.BuyStopLimit,
  VybeOrderType.SellStopLimit,
]);

const PENDING_ENTRY_DRAG_ORDER_TYPES = new Set<number>([
  VybeOrderType.BuyLimit,
  VybeOrderType.SellLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.SellStop,
]);

export const TERMINAL_ORDER_COMMENT = "Vybe Terminal";
export const DEFAULT_SLIPPAGE = 100;

export function isPendingOrder(orderType: number): boolean {
  return PENDING_ORDER_TYPES.has(orderType);
}

/** Market buy/sell only */
export interface MarketSendInput {
  symbol: string;
  lots: number;
  orderType: VybeOrderType.Buy | VybeOrderType.Sell;
  price: number;
  stopLoss?: number;
  takeProfit?: number;
}

export function buildMarketSendPayload(input: MarketSendInput): Record<string, unknown> {
  const sl = input.stopLoss ?? 0;
  const tp = input.takeProfit ?? 0;
  return {
    Symbol: input.symbol,
    Lots: input.lots,
    OrderType: input.orderType,
    Price: input.price,
    StopLoss: sl,
    TakeProfit: tp,
    Slippage: DEFAULT_SLIPPAGE,
    Comment: TERMINAL_ORDER_COMMENT,
  };
}

export interface LimitStopSendInput {
  symbol: string;
  lots: number;
  orderType: number;
  price: number;
  stopLoss: number;
  takeProfit: number;
  stopLimit?: number;
}

export function buildOrderSendPayload(input: LimitStopSendInput): Record<string, unknown> {
  const base: Record<string, unknown> = {
    Symbol: input.symbol,
    Lots: input.lots,
    OrderType: input.orderType,
    Price: input.price,
    StopLoss: input.stopLoss,
    TakeProfit: input.takeProfit,
    Slippage: DEFAULT_SLIPPAGE,
    Comment: TERMINAL_ORDER_COMMENT,
  };
  if (input.stopLimit != null && input.stopLimit > 0) {
    base.StopLimit = input.stopLimit;
  }
  return base;
}

export interface ModifySlTpInput {
  ticket: OpenedTrade;
  stopLoss?: number;
  takeProfit?: number;
  comment?: string;
}

export function buildModifyPayloadForSlTp(input: ModifySlTpInput): Record<string, unknown> {
  const trade = input.ticket;
  const stopLoss =
    input.stopLoss !== undefined ? input.stopLoss : trade.stopLoss || 0;
  const takeProfit =
    input.takeProfit !== undefined ? input.takeProfit : trade.takeProfit || 0;

  const payload: Record<string, unknown> = {
    Ticket: trade.ticket,
    Symbol: trade.symbol,
    Lots: trade.lots,
    Price: trade.openPrice,
    OrderType: trade.orderType,
    StopLoss: stopLoss,
    TakeProfit: takeProfit,
    // A level of 0 means "not specified" on the wire, which every gateway maps to "leave unchanged" —
    // so removing a level needs the explicit flag or the old value survives and the row re-renders
    // with the SL the trader just deleted (SR Global, 2026-09-21).
    ClearStopLoss: stopLoss <= 0 && (trade.stopLoss ?? 0) > 0,
    ClearTakeProfit: takeProfit <= 0 && (trade.takeProfit ?? 0) > 0,
    Comment:
      input.comment ?? "Modified SL/TP via Vybe Terminal",
  };

  if (trade.orderType === VybeOrderType.BuyStopLimit || trade.orderType === VybeOrderType.SellStopLimit) {
    if (typeof trade.stopLimitPrice === "number") {
      payload.StopLimit = trade.stopLimitPrice;
    }
  }

  return payload;
}

/** Pending entry price drag equivalent */
export function buildModifyPayloadForPendingPrice(trade: OpenedTrade, price: number): Record<string, unknown> {
  if (!PENDING_ENTRY_DRAG_ORDER_TYPES.has(trade.orderType)) {
    throw new Error(`Order ticket ${trade.ticket} cannot change entry price (type ${trade.orderType})`);
  }

  const payload: Record<string, unknown> = {
    Ticket: trade.ticket,
    Symbol: trade.symbol,
    Lots: trade.lots,
    Price: price,
    OrderType: trade.orderType,
    StopLoss: trade.stopLoss || 0,
    TakeProfit: trade.takeProfit || 0,
    Comment: "Modified pending order price via Vybe Terminal",
  };

  if (
    trade.orderType === VybeOrderType.BuyStopLimit ||
    trade.orderType === VybeOrderType.SellStopLimit
  ) {
    payload.StopLimit = trade.stopLimitPrice || 0;
  }

  return payload;
}

type QuoteBidAsk = Pick<import("@/vybe-trader-client/subscribed-symbols/types").Quote, "bid" | "ask">;

/**
 * Clamp a requested partial-close volume to (0, trade.lots] and snap it to
 * the symbol's volume step (when known). Falls back to the full position
 * volume for invalid input.
 */
export function resolveCloseLots(
  trade: OpenedTrade,
  lots?: number,
  volumeStep?: number
): number {
  if (
    typeof lots !== "number" ||
    !Number.isFinite(lots) ||
    lots <= 0 ||
    lots >= trade.lots
  ) {
    return trade.lots;
  }
  if (
    typeof volumeStep === "number" &&
    Number.isFinite(volumeStep) &&
    volumeStep > 0
  ) {
    // Snap down to the step grid so we never request more than asked;
    // a sub-step request closes one step.
    const snapped = Math.floor(lots / volumeStep + 1e-9) * volumeStep;
    const rounded = Number(snapped.toFixed(8));
    if (rounded <= 0) return Math.min(volumeStep, trade.lots);
    return Math.min(rounded, trade.lots);
  }
  return lots;
}

/** Close pending or market — matches Chart.tsx executeCloseTrade */
export function buildClosePayload(
  trade: OpenedTrade,
  quote?: QuoteBidAsk | null,
  lots?: number,
  volumeStep?: number
): VybeOrderClosePayload {
  const isPending = isPendingOrder(trade.orderType);

  let price: number;
  if (isPending) {
    price = trade.openPrice;
  } else if (quote) {
    price = getTradeClosePrice(trade, quote);
  } else {
    price = getTradeClosePrice(trade);
  }

  return {
    Ticket: trade.ticket,
    Symbol: trade.symbol,
    Price: price,
    Lots: isPending ? trade.lots : resolveCloseLots(trade, lots, volumeStep),
    OrderType: trade.orderType,
    Slippage: isPending ? 0 : DEFAULT_SLIPPAGE,
  };
}